import type { HikingMetadata } from "~/lib/api";
import { DifficultyBadge } from "../DifficultySelector";
import { DistanceDisplay } from "../DistanceEditor";
import { ElevationDisplay } from "../ElevationEditor";
import { RouteTypeBadge } from "./RouteTypeSelector";
import { StatsLine } from "../StatsLine";

interface TrailStatsProps {
  metadata: HikingMetadata | null | undefined;
  compact?: boolean;
  outline?: boolean;
  className?: string;
}

/**
 * Read-only display of hiking trail stats.
 * Full: "Moderate · 12.5 km · ↑ 450m · Loop"
 * Compact: "Moderate · 12.5 km · Loop" (no elevation)
 */
export function TrailStats({ metadata, compact, outline, className }: TrailStatsProps) {
  if (!metadata) return null;

  const { difficulty, distance, elevation, routeType } = metadata;

  // Check for any elevation (gain OR loss) - supports descent-only trails
  const hasElevation = !!(elevation && (elevation.gain > 0 || elevation.loss > 0));
  const hasStats = difficulty || distance || hasElevation || routeType;

  if (!hasStats) return null;

  // Build aria-label for screen readers
  const ariaLabel = buildAriaLabel({ difficulty, distance, elevation, routeType, hasElevation });

  return (
    <StatsLine className={className} aria-label={ariaLabel}>
      {difficulty && <DifficultyBadge difficulty={difficulty} outline={outline} />}
      {distance && <DistanceDisplay distance={distance} />}
      {hasElevation && !compact && <ElevationDisplay elevation={elevation} />}
      {routeType && <RouteTypeBadge routeType={routeType} />}
    </StatsLine>
  );
}

interface AriaLabelProps {
  difficulty?: string;
  distance?: { value: number; unit: string };
  elevation?: { gain: number; loss: number; unit: string };
  routeType?: string;
  hasElevation: boolean;
}

function buildAriaLabel({ difficulty, distance, elevation, routeType, hasElevation }: AriaLabelProps): string {
  const parts: string[] = [];

  if (difficulty) {
    parts.push(`${difficulty} difficulty`);
  }

  if (distance) {
    const unit = distance.unit === "km" ? "kilometers" : "miles";
    parts.push(`${distance.value} ${unit}`);
  }

  if (hasElevation && elevation) {
    if (elevation.gain > 0 && elevation.loss > 0) {
      const unit = elevation.unit === "m" ? "meters" : "feet";
      if (elevation.gain === elevation.loss) {
        parts.push(`${elevation.gain} ${unit} elevation gain and loss`);
      } else {
        parts.push(`${elevation.gain} ${unit} gain, ${elevation.loss} ${unit} loss`);
      }
    } else if (elevation.gain > 0) {
      const unit = elevation.unit === "m" ? "meters" : "feet";
      parts.push(`${elevation.gain} ${unit} elevation gain`);
    } else if (elevation.loss > 0) {
      const unit = elevation.unit === "m" ? "meters" : "feet";
      parts.push(`${elevation.loss} ${unit} elevation loss`);
    }
  }

  if (routeType) {
    parts.push(`${routeType.replace(/-/g, " ")} route`);
  }

  return `Trail stats: ${parts.join(", ")}`;
}
